import { useState, type SyntheticEvent } from "react";

import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../../lib/apiClient";
import { docUrl } from "../../lib/docLinks";
import { requestErrorDescription } from "../../lib/errors";
import { ApiRequestError } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { useListQuery } from "../../lib/useListQuery";
import {
  Badge,
  Button,
  Checkbox,
  HelpTip,
  Inline,
  InlineError,
  Section,
  Select,
  Spinner,
  Stack,
  Text,
  TextInput,
} from "../../ui";
import {
  PersonaVariableScopeSelect,
  type PersonaVariableOption,
} from "../personas/PersonaVariableScopeSelect";
import {
  initialVariableFormState,
  personaRenameWarning,
  variableFormError,
  variableFormState,
  type ScopeType,
  type Variable,
  type VariableFormState,
} from "./variableFormState";
import { VariableHistory } from "./VariableHistory";
import { VariableValueField } from "./VariableValueField";

export interface VariableScopeOption {
  id: string;
  label: string;
}

export interface VariableEditorProps {
  agentId: string;
  error: string | null;
  initialKey?: string;
  initialScopeId?: string;
  initialScopeType?: ScopeType;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (state: VariableFormState) => Promise<void>;
  personaOptions: PersonaVariableOption[];
  scenarioOptions: VariableScopeOption[];
  variable: Variable | null;
}

function initialState(props: VariableEditorProps): VariableFormState {
  if (props.variable !== null) {
    return variableFormState(props.variable);
  }
  const scopeType = props.initialScopeType ?? "agent";
  const scopeId =
    props.initialScopeId ?? (scopeType === "agent" ? props.agentId : "");
  return initialVariableFormState(scopeType, scopeId, props.initialKey);
}

export function VariableEditor(props: VariableEditorProps) {
  const [state, setState] = useState(() => initialState(props));
  const [localError, setLocalError] = useState<string | null>(null);
  const patch = (partial: Partial<VariableFormState>) => {
    setState((current) => ({ ...current, ...partial }));
  };
  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/me");
      if (error) throw new ApiRequestError(error);
      return data;
    },
  });
  const reservedKeys = me.data?.variable_key_rules.reserved;
  const history = useListQuery("/v1/variables/{id}/revisions", {
    enabled: props.variable !== null,
    pathParams: { id: props.variable?.id ?? "" },
    queryKey: queryKeys.variables.revisions(props.variable?.id ?? ""),
  });

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const validationError = variableFormError(
      state,
      props.variable?.current_revision.is_sensitive === true &&
        state.isSensitive,
      props.variable?.current_revision.is_sensitive === true &&
        !state.isSensitive,
      reservedKeys,
    );
    if (validationError !== null) {
      setLocalError(validationError);
      return;
    }
    setLocalError(null);
    try {
      await props.onSubmit(state);
    } catch (caught) {
      setLocalError(requestErrorDescription(caught));
    }
  }

  const isEditing = props.variable !== null;
  const currentVariable = props.variable;
  const requiresSensitiveReplacement =
    currentVariable?.current_revision.is_sensitive === true &&
    !state.isSensitive;
  const allowedValues = state.allowedValues
    .split(",")
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  const keyWasRenamed =
    currentVariable !== null && state.key.trim() !== currentVariable.key;
  const renameWarning = personaRenameWarning(currentVariable, state);
  const scenarioDefaultOverridesPersona =
    state.scopeType === "scenario" && state.defaultValue.trim().length > 0;
  const keyRulesUnavailable = reservedKeys === undefined;

  return (
    <form
      noValidate
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      <Stack gap={4}>
        {!isEditing ? (
          <Section heading="Value location">
            <Stack gap={3}>
              <Select
                help={
                  <HelpTip
                    docHref={docUrl("concepts/variables")}
                    docLabel="Variable scopes"
                    label="About variable scope"
                  >
                    {
                      "Choose Agent for shared business facts, Persona for caller details, or Scenario for situation inputs. Rows and launch overrides can supply different values later."
                    }
                  </HelpTip>
                }
                label="Scope"
                value={state.scopeType}
                onChange={(event) => {
                  const scopeType = event.currentTarget.value as ScopeType;
                  patch({
                    scopeId: scopeType === "agent" ? props.agentId : "",
                    scopeType,
                  });
                }}
              >
                <option value="agent">Agent constant</option>
                <option value="persona">Persona detail</option>
                <option value="scenario">Scenario input</option>
              </Select>
              {state.scopeType === "persona" ? (
                <PersonaVariableScopeSelect
                  onChange={(scopeId) => {
                    patch({ scopeId });
                  }}
                  options={props.personaOptions}
                  value={state.scopeId}
                />
              ) : null}
              {state.scopeType === "scenario" ? (
                <Select
                  label="Scenario"
                  required
                  value={state.scopeId}
                  onChange={(event) => {
                    patch({ scopeId: event.currentTarget.value });
                  }}
                >
                  <option disabled value="">
                    Select a Scenario
                  </option>
                  {props.scenarioOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              ) : null}
            </Stack>
          </Section>
        ) : null}

        <Section heading="Declaration">
          <Stack gap={3}>
            <TextInput
              label="Key"
              placeholder="disputed_amount"
              required
              value={state.key}
              onChange={(event) => {
                patch({ key: event.currentTarget.value });
              }}
            />
            {keyWasRenamed ? (
              renameWarning === null ? (
                <Text>
                  Agent and Scenario references stay attached when you rename
                  the key.
                </Text>
              ) : (
                <Inline align="center" gap={2}>
                  <Badge tone="warning">Warning</Badge>
                  <Text>{renameWarning}</Text>
                </Inline>
              )
            ) : null}
            <Select
              label="Value type"
              value={state.valueType}
              onChange={(event) => {
                patch({
                  allowedValues: "",
                  defaultValue: "",
                  valueType: event.currentTarget
                    .value as VariableFormState["valueType"],
                });
              }}
            >
              <option value="string">String</option>
              <option value="number">Number</option>
              <option value="bool">Boolean</option>
              <option value="enum">Enum</option>
            </Select>
            {state.valueType === "enum" &&
            currentVariable?.current_revision.is_sensitive &&
            !requiresSensitiveReplacement ? (
              <Stack gap={1}>
                <Text variant="caps">Current allowed values</Text>
                <Text variant="muted">
                  {currentVariable.current_revision.allowed_values === null
                    ? "No allowed values"
                    : "Sensitive — hidden"}
                </Text>
              </Stack>
            ) : state.valueType === "enum" ? (
              <TextInput
                description="Comma-separated values."
                label="Allowed values"
                required
                value={state.allowedValues}
                onChange={(event) => {
                  patch({ allowedValues: event.currentTarget.value });
                }}
              />
            ) : null}
            {currentVariable?.current_revision.is_sensitive &&
            !requiresSensitiveReplacement ? (
              <Stack gap={1}>
                <Text variant="caps">Current default</Text>
                <Text
                  variant={
                    currentVariable.current_revision.default_value === null
                      ? "body"
                      : "muted"
                  }
                >
                  {currentVariable.current_revision.default_value === null
                    ? "No default"
                    : "Sensitive — hidden"}
                </Text>
                <Text variant="muted">
                  The current value stays unchanged when you save.
                </Text>
              </Stack>
            ) : (
              <VariableValueField
                allowedValues={allowedValues}
                emptyLabel="No default"
                help={
                  <HelpTip
                    docHref={docUrl("guides/use-variables-in-simulations")}
                    docLabel="Supply variable values"
                    label="About defaults and required values"
                  >
                    {
                      "Defaults supply values from this scope. A Scenario default overrides Persona values; leave it blank to let a Persona supply the input. Required inputs need a value in every simulation case after defaults, Persona values, rows, and launch overrides are combined."
                    }
                  </HelpTip>
                }
                label="Default value"
                required={requiresSensitiveReplacement}
                value={state.defaultValue}
                valueType={state.valueType}
                onChange={(defaultValue) => {
                  patch({ defaultValue });
                }}
              />
            )}
            <Checkbox
              checked={state.isRequired}
              label="Required in every simulation case"
              onChange={(event) => {
                patch({ isRequired: event.currentTarget.checked });
              }}
            />
            {currentVariable?.current_revision.is_sensitive ? (
              <Checkbox
                checked={state.isSensitive}
                label="Sensitive value"
                onChange={(event) => {
                  patch({ isSensitive: event.currentTarget.checked });
                }}
              />
            ) : null}
            {scenarioDefaultOverridesPersona ? (
              <Inline align="center" gap={2}>
                <Badge tone="warning">Warning</Badge>
                <Text>
                  This default overrides every Persona value for this key.
                </Text>
              </Inline>
            ) : null}
          </Stack>
        </Section>

        {me.isPending ? (
          <Spinner label="Loading variable key rules" />
        ) : keyRulesUnavailable ? (
          <Inline gap={2}>
            <InlineError>{requestErrorDescription(me.error)}</InlineError>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                void me.refetch();
              }}
            >
              Retry
            </Button>
          </Inline>
        ) : null}
        {(localError ?? props.error) ? (
          <InlineError>{localError ?? props.error}</InlineError>
        ) : null}
        {currentVariable !== null ? (
          <VariableHistory
            currentRevisionId={currentVariable.current_revision.id}
            history={history}
          />
        ) : null}
        <Inline justify="end">
          <Button
            disabled={props.isPending}
            type="button"
            variant="secondary"
            onClick={props.onCancel}
          >
            Cancel
          </Button>
          <Button
            disabled={props.isPending || keyRulesUnavailable}
            type="submit"
          >
            {props.isPending ? "Saving…" : "Save variable"}
          </Button>
        </Inline>
      </Stack>
    </form>
  );
}
